import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Calendar, MapPin, Package, RotateCcw, Check } from "lucide-react";

/**
 * Filtros globais persistentes do OWNER (Hermes Admin).
 * Todos os painéis do /admin leem daqui e passam nas suas queries.
 * Persistência via /api/admin/preferences.
 */
const Ctx = createContext(null);

const DEFAULTS = { period: "30d", uf: null, plan: null };
const PERIODS = [
  { k: "7d",   l: "7 dias" },
  { k: "30d",  l: "30 dias" },
  { k: "90d",  l: "90 dias" },
  { k: "365d", l: "12 meses" },
];
const UF_LIST = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG",
  "PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
const PLANS = ["free", "basic", "pro", "enterprise"];

export function GlobalFiltersProvider({ children }) {
  const [filters, setFiltersState] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/preferences");
        if (cancelled) return;
        setFiltersState({
          period: r.data.period || "30d",
          uf: r.data.uf || null,
          plan: r.data.plan || null,
        });
        setSavedAt(r.data.updated_at || null);
      } catch (_e) {
        // usuário não é OWNER — Provider fica com defaults e não persiste
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const persist = useCallback(async (next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const r = await api.put("/admin/preferences", next);
        setSavedAt(r.data.updated_at);
      } catch (_e) {}
    }, 350);
  }, []);

  const setFilters = useCallback((patch) => {
    setFiltersState((prev) => {
      const next = { ...prev, ...patch };
      persist(next);
      return next;
    });
  }, [persist]);

  const reset = useCallback(() => setFilters(DEFAULTS), [setFilters]);

  const value = useMemo(() => ({
    ...filters, loaded, savedAt,
    setPeriod: (period) => setFilters({ period }),
    setUF:     (uf)     => setFilters({ uf }),
    setPlan:   (plan)   => setFilters({ plan }),
    reset,
    // Helpers para as telas montarem as query-strings
    queryParams: () => {
      const p = new URLSearchParams();
      if (filters.period) p.set("period", filters.period);
      if (filters.uf) p.set("uf", filters.uf);
      if (filters.plan) p.set("plan", filters.plan);
      return p.toString();
    },
  }), [filters, loaded, savedAt, setFilters, reset]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGlobalFilters() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useGlobalFilters must be inside <GlobalFiltersProvider>");
  return v;
}

export function GlobalFiltersBar() {
  const gf = useGlobalFilters();
  return (
    <div className="sticky top-0 z-30 border-b border-white/5 bg-[#0a0d17]/95 backdrop-blur px-6 py-3"
         data-testid="admin-global-filters">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mr-1">Filtros globais</span>

        {/* Período */}
        <div className="flex items-center gap-1 bg-[#0f1524] border border-white/5 rounded-lg p-1"
             data-testid="gf-period">
          <Calendar className="w-3.5 h-3.5 text-slate-400 ml-1"/>
          {PERIODS.map(p => (
            <button key={p.k}
              onClick={() => gf.setPeriod(p.k)}
              data-testid={`gf-period-${p.k}`}
              className={`px-2.5 py-1 rounded-md text-[11px] transition-colors ${
                gf.period === p.k
                  ? "bg-[#d4a24c] text-black font-semibold"
                  : "text-slate-300 hover:bg-white/5"
              }`}>{p.l}</button>
          ))}
        </div>

        {/* UF */}
        <label className="flex items-center gap-1.5 bg-[#0f1524] border border-white/5 rounded-lg px-2 py-1.5"
               data-testid="gf-uf-wrapper">
          <MapPin className="w-3.5 h-3.5 text-slate-400"/>
          <select
            value={gf.uf || ""}
            onChange={(e) => gf.setUF(e.target.value || null)}
            className="bg-transparent text-slate-200 text-[11px] outline-none pr-1"
            data-testid="gf-uf">
            <option value="">Brasil (todas UFs)</option>
            {UF_LIST.map(uf => <option key={uf} value={uf} className="bg-[#0f1524]">{uf}</option>)}
          </select>
        </label>

        {/* Plano */}
        <label className="flex items-center gap-1.5 bg-[#0f1524] border border-white/5 rounded-lg px-2 py-1.5"
               data-testid="gf-plan-wrapper">
          <Package className="w-3.5 h-3.5 text-slate-400"/>
          <select
            value={gf.plan || ""}
            onChange={(e) => gf.setPlan(e.target.value || null)}
            className="bg-transparent text-slate-200 text-[11px] outline-none pr-1"
            data-testid="gf-plan">
            <option value="">Todos os planos</option>
            {PLANS.map(p => <option key={p} value={p} className="bg-[#0f1524]">{p}</option>)}
          </select>
        </label>

        <button onClick={gf.reset}
          className="flex items-center gap-1 text-slate-400 hover:text-slate-100 text-[11px] ml-1"
          data-testid="gf-reset">
          <RotateCcw className="w-3.5 h-3.5"/> Redefinir
        </button>

        {gf.savedAt && (
          <span className="ml-auto flex items-center gap-1 text-[10px] text-emerald-400/80"
                data-testid="gf-saved">
            <Check className="w-3 h-3"/> Salvo
          </span>
        )}
      </div>
    </div>
  );
}
